import {
  ColDef,
  GridOptions,
  ProcessCellForExportParams,
  ValueGetterParams,
} from '@ag-grid-community/core';

import {
  BalanceSheetEntitySettingsKeys,
  BalanceSheetEntitySettingsLabels,
} from '@widgets/new-manage-balance-sheets-accounts-modal/components/balance-sheet-account-grid/grid-config/grid-config-context';

import { genericGetAttributeColumns } from '@features/budget-attributes/attribute-columns';

import { PivotSettings } from '@models/trial-attributes/trial-attributes';

import {
  AgRawMultiTooltip,
  AgRawMultiTooltipValues,
} from '@shared/components/ag-raw-tooltip/ag-raw-multi-tooltip';
import { Utils } from '@shared/utils/utils';

import { forcedTooltip } from './balance-sheet-account-row-data.util';
import { DEFAULT_BALANCE_SHEET_ACCOUNTS_GRID_CONFIG } from './default-grid-config';
import {
  BalanceSheetAccountsGridConfigOptions,
  BalanceSheetAccountView,
} from './grid-config-context';

const getBalanceSheetAttributeColumns = (
  pivotSettings: PivotSettings,
  gridData: BalanceSheetAccountView[]
): ColDef<BalanceSheetAccountView>[] => {
  if (!pivotSettings.attributes || pivotSettings.attributes.length === 0) {
    return [];
  }

  return genericGetAttributeColumns({
    attributeConfig: {
      attributes: pivotSettings.attributes,
      gridData,
      primaryAttribute: pivotSettings.primary,
      secondaryAttribute: pivotSettings.secondary,
    },
    colDefOverrides: {
      editable: false,
      tooltipComponent: AgRawMultiTooltip,
      tooltipField: undefined,
    },
    onlyPivot: false,
  }).map(
    (col) =>
      <ColDef>{
        ...col,
        field: `attributes.${col.field}`,
        suppressSizeToFit: true,
      }
  );
};

const compareVendorNames = (nameA: string, nameB: string): number => {
  if (!nameA && !nameB) return 0;

  if (!nameA) return -1;

  if (!nameB) return 1;

  return Utils.localeAlphaNumSort(nameA, nameB);
};

const getTooltipValueForFormattedColumn = () =>
  <AgRawMultiTooltipValues>[
    {
      type: 'overflow',
      use: 'valueFormatted',
    },
    {
      type: 'force-tooltip',
      value: forcedTooltip,
    },
  ];

const getStaticPreAttributeColumns = (
  vendorIdToNameMap: Map<string, string>
): ColDef<BalanceSheetAccountView>[] => [
  {
    comparator: (valueA, valueB, nodeA, nodeB) => {
      // For filtering, nodes won't be available, so use values directly
      if (!nodeA || !nodeB) {
        const nameA = valueA || '';
        const nameB = valueB || '';

        return compareVendorNames(nameA, nameB);
      }

      // For sorting, use node data
      const nameA = (nodeA.data?.vendorId && vendorIdToNameMap.get(nodeA.data.vendorId)) || '';
      const nameB = (nodeB.data?.vendorId && vendorIdToNameMap.get(nodeB.data.vendorId)) || '';

      return compareVendorNames(nameA, nameB);
    },
    field: 'vendorId',
    filterValueGetter: (params: ValueGetterParams<BalanceSheetAccountView>) => {
      if (
        typeof params.data?.vendorId === 'string' &&
        vendorIdToNameMap.has(params.data.vendorId)
      ) {
        return vendorIdToNameMap.get(params.data.vendorId);
      }

      return '';
    },
    headerName: 'Vendor Contract',
    minWidth: 200,
    pinned: 'left',
    sort: 'asc',
    tooltipValueGetter: getTooltipValueForFormattedColumn,
    valueFormatter: (params) => {
      if (typeof params.value === 'string' && vendorIdToNameMap.has(params.value)) {
        return vendorIdToNameMap.get(params.value);
      }

      return params.value;
    },
  },
  {
    field: 'type',
    filterValueGetter: (params: ValueGetterParams<BalanceSheetAccountView>) => {
      if (!params.data) return '';

      return BalanceSheetEntitySettingsLabels[
        params.data.type as keyof typeof BalanceSheetEntitySettingsKeys
      ];
    },
    headerName: 'Balance Sheet Account',
    minWidth: 250,
    tooltipValueGetter: getTooltipValueForFormattedColumn,
    valueFormatter: (params) => {
      const value = params.value as keyof typeof BalanceSheetEntitySettingsKeys;

      return BalanceSheetEntitySettingsLabels[value];
    },
  },
];

const getStaticPostAttributeColumns = (): ColDef<BalanceSheetAccountView>[] => [
  {
    field: 'program',
    headerName: 'Program *',
    minWidth: 150,
  },
  {
    field: 'project',
    headerName: 'Project *',
    minWidth: 150,
  },
];

export const balanceSheetAccountsGridConfig = (
  options: BalanceSheetAccountsGridConfigOptions
): GridOptions<BalanceSheetAccountView> => {
  const { gridData, pivotSettings, vendorIdToNameMap } = options;

  let attributeColumns: ColDef<BalanceSheetAccountView>[] = [];

  if (pivotSettings.attributes.length > 0) {
    attributeColumns = getBalanceSheetAttributeColumns(pivotSettings, gridData);
  }

  // Add processCellForClipboard to use display values for vendorId and type
  const processCellForClipboard = (params: ProcessCellForExportParams) => {
    if (params.column.getColId() === 'vendorId') {
      return vendorIdToNameMap.get(params.value) ?? params.value;
    }

    if (params.column.getColId() === 'type') {
      return (
        BalanceSheetEntitySettingsLabels[
          params.value as keyof typeof BalanceSheetEntitySettingsLabels
        ] ?? params.value
      );
    }

    return params.value;
  };

  return {
    ...DEFAULT_BALANCE_SHEET_ACCOUNTS_GRID_CONFIG,
    columnDefs: [
      ...getStaticPreAttributeColumns(vendorIdToNameMap),
      ...attributeColumns,
      ...getStaticPostAttributeColumns(),
    ],
    processCellForClipboard,
  };
};
